import React, { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { signOut } from "firebase/auth";
import { auth } from "../../firebase";
import { doc, getDoc } from "firebase/firestore";
import { firestore  } from "../../firebase";
import HeaderLogo from '../../assets/images/header-logo.png';

const Navbar = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const dropdownRef = useRef(null); // ✅ Reference for outside click
  const [userProfile, setUserProfile] = useState(null);

  // users/{uid} stores a single `role` string - see SHARED.md §3. The old
  // roles[] array model was retired in Sprint 3c; both rule sets and every
  // client now agree on role == "admin".
  const hasRole = (role) => userProfile?.role === role;

    // platform flyout (left side)
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const flyoutRef = useRef(null);

  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  useEffect(() => {
    const handler = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/");
    } catch (err) {
      alert("Logout failed: " + err.message);
    }
  };

  // ✅ Close dropdown if clicked outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Loads users/{uid} for the avatar and the role check. Every branch must
  // end in a definite value: leaving userProfile untouched on logout, on a
  // missing document, or on error is what strands the previous account's
  // avatar in the navbar. See SHARED.md §10.
  //
  // A second, earlier copy of this effect used to sit above it and returned
  // early on logout without clearing. It doubled the Firestore reads on every
  // mount and raced this one on login. Removed.
  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) {
        setUserProfile(null);
        return;
      }

      try {
        const docRef = doc(firestore, "users", user.uid);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          setUserProfile(docSnap.data());
        } else {
          setUserProfile(null);
        }
      } catch (err) {
        console.error("Failed to load user profile:", err);
        setUserProfile(null);
      }
    };

    fetchProfile();
  }, [user]);

  const closeFlyoutAndNavigate = (path) => {
    setFlyoutOpen(false);
    navigate(path);
  };


  return (

    <nav style={navStyle}>
      {/* LEFT: hamburger + logo */}
      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flex: "1 1 0" }}>
        <div style={{ position: "relative" }} ref={flyoutRef}>
          <button
            type="button"
            aria-label="Open menu"
            aria-expanded={flyoutOpen}
            onClick={() => setFlyoutOpen((v) => !v)}
            style={hamburgerButton}
          >
            <span style={hamburgerLine} />
            <span style={hamburgerLine} />
            <span style={hamburgerLine} />
          </button>

          {/* overlay (clickable backdrop) */}
          {flyoutOpen && (
            <div
              style={flyoutOverlay}
              onClick={() => setFlyoutOpen(false)}
              aria-hidden="true"
            />
          )}

          {/* flyout drawer */}
          {flyoutOpen && (
            <div style={flyoutDrawer}>
              <div style={flyoutHeader}>
                <img src={HeaderLogo} alt="Antarctic Press" style={{ height: "40px", width: "auto" }} />
              </div>
              <a href="/" style={flyoutLink} onClick={() => setFlyoutOpen(false)}>Home</a>
              <a href="/fund" style={flyoutLink} onClick={() => setFlyoutOpen(false)}>Fund Go</a>
              <a href="/reader" style={flyoutLink} onClick={() => setFlyoutOpen(false)}>AP Digital Library</a>
              <a href="/shop" style={flyoutLink} onClick={() => setFlyoutOpen(false)}>AP Shop</a>
{/*               {hasRole("admin") && (
                <a
                  href="/admin"
                  style={flyoutLink}
                  onClick={() => setFlyoutOpen(false)}
                >
                  Admin
                </a>
              )} */}
            </div>
          )}
        </div>

        {/* Logo goes to platform home */}
        <NavLink to="/" style={{ display: "inline-flex", alignItems: "center" }}>
          <img src={HeaderLogo} height="40" alt="Antarctic Press" />
        </NavLink>
      </div>

      {/* CENTER: inline nav links — desktop only */}
      <div style={{ display: isDesktop ? "flex" : "none", alignItems: "center", gap: "24px", flex: "0 0 auto" }}>
        {[
          { label: "Home",             href: "/"                      },
          { label: "Fund Go",          href: "/fund"                  },
          { label: "Digital Library",  href: "/reader"                },
          { label: "Shop",             href: "/shop"                  },
          { label: "Submissions",      href: "/portal/submissions"    },
          { label: "Retailers",        href: "/portal/retailers"      },
          { label: "About",            href: "/portal/about"          },
        ].map(({ label, href }) => (
          <a
            key={href}
            href={href}
            style={inlineNavLinkStyle}
            onMouseEnter={(e) => { e.currentTarget.style.color = "#43c6f9"; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = "#ffffff"; }}
          >
            {label}
          </a>
        ))}
      </div>

<div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", flex: "1 1 0" }}>
  {user ? (
    <>
      <div style={{ position: "relative" }} ref={dropdownRef}>
        <button
          onClick={() => setMenuOpen((prev) => !prev)}
          style={dropdownToggle}
        >
          {userProfile?.avatar || user?.photoURL ? (
            <img
              src={userProfile?.avatar || user?.photoURL}
              alt="avatar"
              style={{
                width: 30,
                height: 30,
                borderRadius: "50%",
                marginRight: "0.5rem",
                verticalAlign: "middle",
              }}
            />
          ) : (
            <span style={{ marginRight: "0.5rem" }}>{user?.email}</span>
          )}
          ▼
        </button>

        {menuOpen && (
          <div style={dropdownMenu}>
            <button onClick={handleLogout} style={dropdownItem}>
              Logout
            </button>
          </div>
        )}
      </div>
    </>
  ) : (
    <NavLink to="/portal/login" style={navLinkStyle}>
      Login
    </NavLink>
  )}
</div>

    </nav>
  );
};
// The three groups centre the middle one by making the left and right groups
// equal width (flex: 1 1 0 each), NOT by space-between. space-between only
// equalises the two gaps, which lands the middle group at
// centre + (leftWidth - rightWidth) / 2 - and the left group carries the logo
// while the right carries only an avatar, so it was always pushed right.
const navStyle = {
  padding: "1rem",
  background: "#222",
  color: "#fff",
  display: "flex",
  alignItems: "center",
};

const navLinkStyle = ({ isActive }) => ({
  marginRight: "1rem",
  textDecoration: "none",
  color: isActive ? "#ffffff" : "#61dafb",
  fontWeight: isActive ? "bold" : "normal",
  borderBottom: isActive ? "2px solid #ffffff" : "none",
});

const dropdownToggle = {
  background: "none",
  color: "#61dafb",
  border: "none",
  cursor: "pointer",
  fontSize: "1rem",
};

const dropdownMenu = {
  position: "absolute",
  right: 0,
  top: "2rem",
  backgroundColor: "#333",
  border: "1px solid #444",
  borderRadius: "4px",
  padding: "0.5rem 0",
  minWidth: "150px",
  zIndex: 2000,
};

const dropdownItem = {
  background: "none",
  border: "none",
  color: "#fff",
  textAlign: "left",
  padding: "0.5rem 1rem",
  width: "100%",
  cursor: "pointer",
};

// Spacing comes from the container's gap, not a per-link marginRight - a
// trailing margin on the last link widened the group by 24px and shifted the
// visible links 12px left of the group's own centre.
const inlineNavLinkStyle = {
  color: "#ffffff",
  fontWeight: "bold",
  fontSize: "1rem",
  textDecoration: "none",
  cursor: "pointer",
  letterSpacing: "0.02em",
};

/* Hamburger + Flyout styles */
const hamburgerButton = {
  width: 40,
  height: 36,
  display: "inline-flex",
  flexDirection: "column",
  justifyContent: "center",
  gap: 5,
  background: "none",
  border: "1px solid #444",
  borderRadius: 6,
  cursor: "pointer",
  padding: "0 10px",
};

const hamburgerLine = {
  display: "block",
  height: 2,
  width: "100%",
  background: "#61dafb",
};

const flyoutOverlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.45)",
  zIndex: 1500,
};

const flyoutDrawer = {
  position: "fixed",
  top: 0,
  left: 0,
  height: "100vh",
  width: 260,
  background: "#111",
  borderRight: "1px solid #333",
  zIndex: 1600,
  padding: "1rem",
  display: "flex",
  flexDirection: "column",
  gap: "0.5rem",
};

const flyoutHeader = {
  fontWeight: "bold",
  color: "#fff",
  marginBottom: "0.5rem",
  paddingBottom: "0.5rem",
  borderBottom: "1px solid #333",
};

const flyoutLink = {
  display: "block",
  textDecoration: "none",
  border: "1px solid #333",
  borderRadius: 8,
  color: "#61dafb",
  padding: "0.6rem 0.75rem",
};


export default Navbar;
